import { useState, useEffect, useCallback } from 'react';
import {
  RawGaushala,
  NormalizedGaushala,
  UseGaushalaMapState,
  GaushalaMapStatistics,
  GeocodingProgress,
} from '../types/gaushalaMap.types';
import {
  calculateStateStatistics,
  calculateGeocodingStatistics,
} from '../services/dataTransformService';
import { useGeocoding } from './useGeocoding';

const GAUSHALA_CACHE_KEY = 'map_gaushala_cache';
const MAP_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function readGaushalaCache(): NormalizedGaushala[] | null {
  try {
    const raw = localStorage.getItem(GAUSHALA_CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > MAP_CACHE_TTL) return null;
    return data as NormalizedGaushala[];
  } catch { return null; }
}

function writeGaushalaCache(data: NormalizedGaushala[]) {
  try { localStorage.setItem(GAUSHALA_CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Custom Hook: useGaushalaMap
 *
 * Main hook that orchestrates the entire gaushala map data flow:
 * 1. Fetches gaushalas from API
 * 2. Normalizes data (geocoding if needed)
 * 3. Calculates statistics
 * 4. Provides state and error handling
 *
 * Usage:
 * const { gaushalas, isLoading, isGeocoding, progress, error, statistics, refetch } = useGaushalaMap();
 */

const EMPTY_STATISTICS: GaushalaMapStatistics = {
  total: 0, fromDatabase: 0, geocoded: 0, byState: {},
};

export const useGaushalaMap = () => {
  const cachedGaushalas = readGaushalaCache();
  const [state, setState] = useState<UseGaushalaMapState>({
    gaushalas: cachedGaushalas ?? [],
    isLoading: cachedGaushalas === null, // false = show cached data immediately
    isGeocoding: false,
    progress: null,
    error: null,
    statistics: cachedGaushalas
      ? { total: cachedGaushalas.length, fromDatabase: cachedGaushalas.length, geocoded: 0, byState: {} }
      : EMPTY_STATISTICS,
  });
  const { normalizeGaushalas, isGeocoding, progress, error: geocodingError, reset } =
    useGeocoding();

  /**
   * Calculate all statistics from normalized gaushalas
   */
  const calculateStatistics = useCallback(
    (gaushalas: NormalizedGaushala[]): GaushalaMapStatistics => {
      const geoStats = calculateGeocodingStatistics(gaushalas);
      const stateStats = calculateStateStatistics(gaushalas);

      return {
        total: gaushalas.length,
        fromDatabase: geoStats.fromDatabase,
        geocoded: geoStats.geocoded,
        byState: stateStats,
      };
    },
    []
  );

  /**
   * Fetch gaushalas from API
   */
  const fetchGaushalas = useCallback(async (): Promise<RawGaushala[]> => {
    try {
      console.log('[useGaushalaMap] Fetching gaushalas from API...');

      // Try to get auth token
      const token = localStorage.getItem('saubhagya_jwt_token');

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/gaushala-service/api/v1/gaushalas', {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch gaushalas: ${response.status}`);
      }

      const data = await response.json();

      // Handle different API response formats
      let gaushalas: RawGaushala[] = [];

      if (Array.isArray(data)) {
        gaushalas = data;
      } else if (data.content && Array.isArray(data.content)) {
        // Paginated response
        gaushalas = data.content;
      } else if (data.data && Array.isArray(data.data)) {
        // Data wrapper response
        gaushalas = data.data;
      }

      console.log(`[useGaushalaMap] Fetched ${gaushalas.length} gaushalas`);
      return gaushalas;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch gaushalas';
      console.error('[useGaushalaMap] Fetch error:', errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Main data loading and normalization process
   */
  const loadGaushalas = useCallback(async () => {
    try {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      reset();

      // Step 1: Fetch raw gaushalas
      const rawGaushalas = await fetchGaushalas();

      if (rawGaushalas.length === 0) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          gaushalas: [],
          statistics: INITIAL_STATE.statistics,
        }));
        return;
      }

      // Step 2: Normalize gaushalas (geocoding if needed)
      const normalized = await normalizeGaushalas(rawGaushalas);

      // Step 3: Calculate statistics
      const statistics = calculateStatistics(normalized);

      // Step 4: Cache and update state
      writeGaushalaCache(normalized);
      setState((prev) => ({
        ...prev,
        gaushalas: normalized,
        statistics,
        isLoading: false,
        error: geocodingError || null,
      }));

      console.log('[useGaushalaMap] Successfully loaded and normalized gaushalas');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error';
      console.error('[useGaushalaMap] Error:', errorMessage);

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [fetchGaushalas, normalizeGaushalas, calculateStatistics, geocodingError, reset]);

  /**
   * Update geocoding state based on hook state
   */
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      isGeocoding,
      progress,
    }));
  }, [isGeocoding, progress]);

  useEffect(() => {
    loadGaushalas();
  }, [loadGaushalas]);

  /**
   * Refetch gaushalas
   */
  const refetch = useCallback(() => {
    console.log('[useGaushalaMap] Refetching gaushalas...');
    loadGaushalas();
  }, [loadGaushalas]);

  /**
   * Filter gaushalas by state
   */
  const filterByState = useCallback(
    (stateName: string): NormalizedGaushala[] => {
      return state.gaushalas.filter((g) => {
        const addressParts = g.address.split(',');
        const stateMatch = addressParts.some((part) =>
          part.toLowerCase().includes(stateName.toLowerCase())
        );
        return stateMatch;
      });
    },
    [state.gaushalas]
  );

  /**
   * Get gaushalas by data source
   */
  const filterByDataSource = useCallback(
    (dataSource: string): NormalizedGaushala[] => {
      return state.gaushalas.filter((g) => g.dataSource === dataSource);
    },
    [state.gaushalas]
  );

  /**
   * Search gaushalas by name
   */
  const searchByName = useCallback(
    (query: string): NormalizedGaushala[] => {
      const lowerQuery = query.toLowerCase();
      return state.gaushalas.filter(
        (g) =>
          g.name.toLowerCase().includes(lowerQuery) ||
          g.address.toLowerCase().includes(lowerQuery)
      );
    },
    [state.gaushalas]
  );

  /**
   * Get single gaushala by ID
   */
  const getGaushalaById = useCallback(
    (id: number): NormalizedGaushala | null => {
      return state.gaushalas.find((g) => g.id === id) || null;
    },
    [state.gaushalas]
  );

  /**
   * Export state and utility functions
   */
  return {
    // State
    gaushalas: state.gaushalas,
    isLoading: state.isLoading,
    isGeocoding: state.isGeocoding,
    progress: state.progress,
    error: state.error,
    statistics: state.statistics,

    // Functions
    refetch,
    filterByState,
    filterByDataSource,
    searchByName,
    getGaushalaById,
  };
};

export default useGaushalaMap;
